import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  Injector,
  input,
  type OnInit,
  output,
  signal,
  untracked,
  ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { type Observable, firstValueFrom, shareReplay, take } from 'rxjs';
import { Router } from '@angular/router';
import { BrnDialogRef } from '@spartan-ng/brain/dialog';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideArrowRight, lucideChevronRight } from '@ng-icons/lucide';
import { OwnCatalogBadgeComponent } from '@lite-workspace/shared/blocks/own-catalog-row';
import { IdentityStore } from '@lite-workspace/shared/identity';
import {
  LabSearchFieldComponent,
  findPartnerLab,
  labOptionValue,
  resolveLabPick,
  toLabOptions,
} from '@lite-workspace/shared/blocks/lab-search-field';
import { ConfirmDialogComponent, type ConfirmDialogContext } from '@lite-workspace/shared/helm-ui/dialog';
import { pluralRu } from '@lite-workspace/shared/format';
import { resolvePackSizeLabelFromTemplates, type BookFormat } from '@lite-workspace/photobook-model';
import type { ProjectFlowMode } from '@lite-workspace/dto-fotoclass-book';
import { isTrumeauKind, printProductKindLabel, productTitle } from '@lite-workspace/dto-design-system';
import { HlmInputImports } from '@lite-workspace/shared/helm-ui/input';
import { HlmFieldError } from '@lite-workspace/shared/helm-ui/field';
import { HlmButton } from '@lite-workspace/shared/helm-ui/button';
import { HlmDropdownMenuImports } from '@lite-workspace/shared/helm-ui/dropdown-menu';
import type { PrintProductEntry, PrintServiceEntry } from '../domain/infrastructure/print-services-api.service';
import { HlmDialogOpener } from '@lite-workspace/shared/helm-ui/dialog';
import { SelectButtonComponent } from '@lite-workspace/shared/helm-ui/select-button';
import { ChipComponent } from '@lite-workspace/shared/blocks/chip';
import { HlmHint } from '@lite-workspace/shared/helm-ui/hint';
import { FormRowComponent } from '@lite-workspace/shared/blocks/form-row';
import { CounterComponent } from '@lite-workspace/shared/blocks/counter';
import {
  WIZARD_SPLIT_FOOT_CLASS,
  WIZARD_SPLIT_INPUT_CLASS,
  WIZARD_SPLIT_ROWS_CLASS,
} from '@lite-workspace/shared/blocks/wizard-shell';
import { CreateProjectPreferencesService } from '../domain/application/create-project-preferences.service';
import {
  CreateProjectDraftStashService,
  type CreateProjectDraftSnapshot,
  requestOf,
} from '../domain/application/create-project-draft-stash.service';
import {
  DEFAULT_SPREADS_TARGET,
  DefaultPrintProductService,
  pickPreferredProduct,
} from '../domain/application/default-print-product.service';
import { PrintCatalogFacade } from '../domain/application/print-catalog.facade';
import { ProductCascade } from '../domain/application/product-cascade';
import {
  packGateProductFormat,
  packPrintProductId,
  PrintProductFormatService,
  printProductFormat,
} from '../domain/application/print-product-format.service';
import { formatLabel, technologyLabel, type ProductVariantGroup } from '../domain/application/product-variants';
import { ProjectsFacade } from '../domain/application/projects.facade';
import { PROJECT_NAME_ERROR } from '../domain/infrastructure/projects-api.service';
import { TelegramContactService } from '../../../ui/telegram-contact';
import { OwnProductUsageService, ownCatalogDeleteWarning } from '../../printers/domain/application/own-product-usage.service';
import type { OwnPrintProduct } from '@lite-workspace/shared/blocks/own-print-product';
import { FREE_PACK_ID, WizardAnalyticsService, type TemplateSelectTrigger } from '../../../core/wizard-analytics.service';
import { ToastService } from '@lite-workspace/shared/helm-ui/toast';
import { DesignsFacade } from '../../designs/domain/application/designs.facade';
import { PartnerShopResource } from '../../designs/domain/infrastructure/partner-shop.resource';
import { LumirLibraryResource } from '../../designs/domain/infrastructure/partner-showcase.resource';
import {
  designPackFormat,
  formatsConflict,
  FREE_DESIGN_COVER,
  restorableDesign,
  type DesignSummary,
  type LumirLibraryPack,
  type MyTemplate,
} from '../../designs/domain/entities/design.models';
import { libraryOffer } from './template-picker-cards';
import {
  TemplatePickerDialogComponent,
  type TemplatePickerDialogContext,
  type TemplatePickerResult,
} from './template-picker-dialog.component';

/** What the modal hands back on «Продолжить» — the project draft the wizard is launched with. */
export interface CreateProjectDraft {
  name: string;
  service: string;
  product: string;
  spreads: number;
  /** Выбранный продукт типографии целиком — источник printProductId/границ для baseSettings проекта. */
  productEntry: PrintProductEntry | null;
  /** Новый проект всегда «Макеты собирают ученики»; режим меняется в настройках проекта. */
  flowMode: ProjectFlowMode;
  /** Выбранный шаблон; null — «Без шаблона». */
  designId: string | null;
  /** CRM school request the project is born from (`?fromRequest=`); the project id is written back to it. */
  fromRequest?: string;
}

/** `sheet` — внутри модалки «Создание проекта»; `step` — шаг мастера, шаблон ведёт его панель. */
export type CreateProjectFormChrome = 'sheet' | 'step';

const FORMAT_HINT_MS = 5000;

function clampSpreads(spreads: number, product: PrintProductEntry): number {
  return Math.min(Math.max(spreads, product.minSpreads), product.maxSpreads);
}

@Component({
  selector: 'app-create-project-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    HlmButton,
    HlmInputImports,
    HlmFieldError,
    HlmDropdownMenuImports,
    SelectButtonComponent,
    ChipComponent,
    HlmHint,
    FormRowComponent,
    CounterComponent,
    OwnCatalogBadgeComponent,
    LabSearchFieldComponent,
    NgIcon,
  ],
  viewProviders: [provideIcons({ lucideArrowRight, lucideChevronRight })],
  host: { class: 'contents' },
  template: `
    <div [class]="step() ? stepRowsClass : 'flex flex-col gap-5'">
      <app-form-row label="Название проекта" [size]="rowSize()">
        <div class="flex flex-col gap-1">
          <input
            hlmInput
            type="text"
            placeholder="Новый проект"
            autocomplete="off"
            [value]="name()"
            (input)="onNameInput($any($event.target).value)"
            (keydown.enter)="submit()"
            autofocus
            [class]="step() ? stepInputClass : 'h-10.5 w-full'"
            [class.border-bad]="nameError()"
          />
          @if (nameError(); as error) {
            <hlm-field-error forceShow size="xs">{{ error }}</hlm-field-error>
          }
        </div>
      </app-form-row>

      @if (step()) {
        <hr class="my-0.5 border-0 border-t border-line2" />
        <div class="mt-1.5 -mb-2 text-xs font-semibold tracking-[var(--ls-caps-m)] text-c3 uppercase">Печать</div>
      }

      <!-- Меченый аккаунт печатает только в своей типографии: выбирать нечего. -->
      @if (!partnerLab() && !labPending()) {
        <app-form-row label="Типография" [size]="rowSize()" align="start">
          <div class="flex flex-col gap-1" (focusin)="retryLoadIfNeeded()">
            <app-lab-search-field
              [options]="labOptions()"
              [value]="labValue()"
              addable
              supportable
              editable
              removable
              [invalid]="serviceError()"
              [disabled]="servicesLoading()"
              (valueChange)="pickLab($event)"
              (addOwn)="goToOwnCatalog($event)"
              (support)="requestOtherService()"
              (editOwn)="editOwnLab($event)"
              (removeOwn)="removeOwnLab($event)"
            />
            @if (serviceError()) {
              <span class="text-xs text-bad">Выберите типографию</span>
            } @else if (catalogFailed()) {
              <span class="text-xs text-bad">Каталог типографий не загрузился</span>
            } @else if (productError()) {
              <span class="text-xs text-bad">Не удалось загрузить типографии — проверьте соединение и попробуйте ещё раз</span>
            }
          </div>
        </app-form-row>
      }

      @if (cascade.kindVisible()) {
        <app-form-row label="Вид изделия" [size]="rowSize()">
          <button app-select-button [hlmDropdownMenuTrigger]="kindMenu" align="start" side="bottom">
            {{ kindLabel() }}
          </button>
        </app-form-row>
      }

      @if (cascade.nameVisible()) {
        <app-form-row label="Продукт" [size]="rowSize()" align="start">
          <div class="flex flex-col gap-1">
            <button
              app-select-button
              [hlmDropdownMenuTrigger]="nameMenu"
              align="start"
              side="bottom"
              (hlmDropdownMenuOpened)="productMenuOpen.set(true)"
              (hlmDropdownMenuClosed)="productMenuOpen.set(false)"
            >
              {{ nameLabel() }}
            </button>
            <!-- Строка начинается вровень с текстом в поле (рамка и отступ кнопки). Под открытым списком
                 она прячется, не сдвигая ряды: иначе край буквы виден у скруглённого угла списка. -->
            @if (cascade.technologyCaption(); as technology) {
              <span data-testid="product-technology" class="ml-3.75 text-xs text-c3" [class.invisible]="productMenuOpen()">{{
                technology
              }}</span>
            }
          </div>
        </app-form-row>
      }

      @if (cascade.technologyVisible()) {
        <app-form-row label="Технология" [size]="rowSize()" [align]="chipRowAlign()">
          <div [class]="step() ? 'flex flex-wrap gap-2' : 'flex flex-wrap gap-1.5'">
            @for (tech of cascade.technologyOptions(); track tech) {
              <app-chip
                [size]="chipSize()"
                [label]="technologyLabelOf(tech)"
                [active]="tech === cascade.technologyValue()"
                (selected)="cascade.selectTechnology(tech)"
              />
            }
          </div>
        </app-form-row>
      }

      @if (cascade.formatVisible()) {
        <app-form-row label="Формат" [size]="rowSize()" [align]="chipRowAlign()">
          <div [class]="step() ? 'flex flex-wrap gap-2' : 'flex flex-wrap gap-1.5'">
            @for (option of cascade.formatGroups(); track option.key) {
              @let hint = formatHints().get(option.key) ?? '';
              <app-chip
                [size]="chipSize()"
                [label]="formatLabelOf(option)"
                [active]="option.key === cascade.selectedGroup()?.key"
                [tone]="hint ? 'dimmed' : 'neutral'"
                [hlmHint]="hint"
                [hlmHintMaxWidth]="320"
                [hlmHintOpen]="!!hint && formatHintAt() === option.key"
                (pointerleave)="onFormatChipLeave($event)"
                (selected)="hint ? toggleFormatHint(option.key) : cascade.selectGroup(option.key)"
              />
            }
          </div>
        </app-form-row>
      }

      @if (cascade.variantVisible()) {
        <app-form-row label="Вариант" [size]="rowSize()" [align]="chipRowAlign()">
          <div [class]="step() ? 'flex flex-wrap gap-2' : 'flex flex-wrap gap-1.5'">
            @for (option of cascade.variantChoice(); track option.id) {
              <app-chip
                [size]="chipSize()"
                [label]="option.label"
                [active]="option.id === cascade.variantValue()"
                (selected)="cascade.selectVariant(option.id)"
              />
            }
          </div>
        </app-form-row>
      }

      @if (cascade.baseVisible()) {
        <app-form-row label="Основа листа" [size]="rowSize()" [align]="chipRowAlign()">
          <div [class]="step() ? 'flex flex-wrap gap-2' : 'flex flex-wrap gap-1.5'">
            @for (hasBase of cascade.baseChoice(); track hasBase) {
              <app-chip
                [size]="chipSize()"
                [label]="hasBase ? 'С основой' : 'Без основы'"
                [active]="hasBase === cascade.baseValue()"
                (selected)="cascade.selectBase(hasBase)"
              />
            }
          </div>
        </app-form-row>
      }

      @if (cascade.thicknessVisible()) {
        <app-form-row label="Толщина основы" [size]="rowSize()" [align]="chipRowAlign()">
          <div [class]="step() ? 'flex flex-wrap gap-2' : 'flex flex-wrap gap-1.5'">
            @for (mm of cascade.thicknessChoice(); track mm) {
              <app-chip
                [size]="chipSize()"
                [label]="mm + ' мм'"
                [active]="mm === cascade.thicknessValue()"
                (selected)="cascade.selectThickness(mm)"
              />
            }
          </div>
        </app-form-row>
      }

      @if (!designRowHidden() && chrome() === 'sheet') {
        <app-form-row label="Шаблон">
          <button
            type="button"
            (click)="openTemplatePicker()"
            class="flex h-14 w-full min-w-0 items-center gap-3 rounded-[var(--r)] border border-line2 py-1.5 pr-2.5 pl-1.5
                   text-left transition-[var(--t-prop-rim-fill)] duration-[var(--t-fast)] ease-[var(--ease-smooth)]
                   hover:border-line3 hover:bg-acc-softest focus-visible:ring-2 focus-visible:ring-acc-line
                   focus-visible:outline-none"
          >
            <span class="h-10.5 w-16 shrink-0 overflow-hidden rounded-[var(--r-s)] bg-bg">
              <img [src]="designCover()" alt="" class="size-full object-cover" />
            </span>
            <span class="min-w-0 flex-1 truncate text-sm font-semibold tracking-[var(--ls-tight)] text-ink">{{
              designName()
            }}</span>
            <ng-icon name="lucideChevronRight" size="18" class="shrink-0 text-c3" />
          </button>
        </app-form-row>
      }

      @if (resolvedProduct() && !singleSpread()) {
        <app-form-row label="Количество разворотов" [size]="rowSize()">
          <div class="flex flex-col gap-1">
            <app-counter
              [value]="spreads()"
              (valueChange)="setSpreads($event)"
              [min]="spreadsMin()"
              [max]="spreadsMax()"
              [step]="spreadStep()"
              [hint]="pagesHint()"
              [size]="rowSize()"
            />
            @if (spreadsLimitHit()) {
              <span class="text-xs text-c3">Для выбранного формата доступно от {{ spreadsMin() }} до {{ spreadsMax() }}</span>
            }
          </div>
        </app-form-row>
      }
    </div>

    <div [class]="step() ? stepFootClass : 'mt-10 flex justify-center gap-3'">
      @if (step()) {
        <!-- Шаг мастера кладёт сюда свои точки шагов. -->
        <ng-content />
      } @else {
        <button hlmBtn type="button" variant="outline" size="dialog" (click)="cancel()">Отменить</button>
      }
      <!-- Выключенная кнопка событий не ловит — подсказку о пропущенном виде держит обёртка. -->
      <span class="inline-flex" [class.ml-auto]="step()" [hlmHint]="submitHint()" hlmHintAlign="center">
        <button
          hlmBtn
          type="button"
          [variant]="submitBlocked() ? 'blocked' : step() ? 'ink' : 'default'"
          [size]="step() ? 'pill-lg' : 'dialog'"
          [disabled]="submitBlocked()"
          [attr.aria-busy]="nameCheckPending()"
          (click)="submit()"
        >
          {{ nameCheckPending() ? 'Проверяем…' : 'Продолжить' }}
          @if (step()) {
            <ng-icon name="lucideArrowRight" size="18" />
          }
        </button>
      </span>
    </div>

    <ng-template #kindMenu>
      <div hlmDropdownMenu class="min-w-55">
        @for (option of cascade.kindOptions(); track option) {
          <button hlmDropdownMenuItem type="button" (triggered)="cascade.selectKind(option)">
            {{ kindLabelOf(option) }}
          </button>
        }
      </div>
    </ng-template>

    <ng-template #nameMenu>
      <div hlmDropdownMenu class="min-w-55">
        @for (option of cascade.nameOptions(); track option) {
          <button hlmDropdownMenuItem type="button" (triggered)="cascade.selectName(option)">
            <span class="min-w-0 flex-1 text-left">
              <span class="block truncate">{{ option }}</span>
              @if (cascade.nameCaptions().get(option); as technology) {
                <span class="mt-0.5 block text-xs text-c3">{{ technology }}</span>
              }
            </span>
            @if (ownProductNames().has(option)) {
              <app-own-catalog-badge label="Мой" />
            }
          </button>
        }
        <div hlmDropdownMenuSeparator></div>
        <button hlmDropdownMenuItem type="button" class="text-c3" (triggered)="requestOtherFormat()">Написать в поддержку</button>
      </div>
    </ng-template>
  `,
})
export class CreateProjectFormComponent implements OnInit {
  // Только модалка: шаг мастера живёт без неё и никого не закрывает.
  private readonly dialogRef = inject(BrnDialogRef, { optional: true });
  private readonly dialogs = inject(HlmDialogOpener);
  private readonly vcr = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly printCatalog = inject(PrintCatalogFacade);
  private readonly defaultPrintProduct = inject(DefaultPrintProductService);
  private readonly projects = inject(ProjectsFacade);
  private readonly telegramContact = inject(TelegramContactService);
  private readonly createProjectPreferences = inject(CreateProjectPreferencesService);
  private readonly designs = inject(DesignsFacade);
  private readonly shop = inject(PartnerShopResource);
  private readonly library = inject(LumirLibraryResource);
  private readonly toast = inject(ToastService);
  private readonly wizardAnalytics = inject(WizardAnalyticsService);
  private readonly draftStash = inject(CreateProjectDraftStashService);
  private readonly router = inject(Router);
  private readonly usage = inject(OwnProductUsageService);
  // Профиль читает приложение само при входе (AnalyticsIdentityService) — форма только ждёт его.
  private readonly identity = inject(IdentityStore);
  private readonly injector = inject(Injector);
  private readonly savedPreference = this.createProjectPreferences.read();

  /** The already-picked design pack's format family — it outranks the saved preference when they clash,
   *  else the form would preselect a product the pack cannot fit. */
  readonly packFormat = input<BookFormat | null>(null);
  /** Стартовый выбор шаблона от вызывающего экрана; null — «Без шаблона», undefined — экран его не задал. */
  readonly initialDesignId = input<string | null | undefined>(undefined);
  /** Экран сам ведёт шаблон своим шагом (онбординг) — строку «Шаблон» скрываем целиком. */
  readonly designRowHidden = input(false);
  /** Оставленный черновик, если форму открыл возврат из «Шаблонов»; он старше любых дефолтов. */
  readonly restore = input<CreateProjectDraftSnapshot | null>(null);
  /** Имя уже созданного проекта, которому форма правит настройки: оно своё, не занятое. */
  readonly ownName = input<string | null>(null);
  readonly chrome = input<CreateProjectFormChrome>('sheet');

  readonly submitted = output<CreateProjectDraft>();
  readonly cancelled = output<void>();

  /** Свой продукт, по которому сейчас считаются проекты: до ответа второе окно не открывается. */
  protected readonly askingDelete = signal<string | null>(null);

  /** Шаг мастера: строки крупнее, форма стоит по центру колонки и прокручивается внутри неё. */
  protected readonly step = computed(() => this.chrome() === 'step');
  protected readonly rowSize = computed(() => (this.step() ? 'lg' : 'md'));
  protected readonly chipSize = computed(() => (this.step() ? 'lg' : 'default'));
  protected readonly chipRowAlign = computed(() => (this.step() ? 'start' : 'center'));
  protected readonly stepRowsClass = WIZARD_SPLIT_ROWS_CLASS;
  protected readonly stepInputClass = WIZARD_SPLIT_INPUT_CLASS;
  protected readonly stepFootClass = WIZARD_SPLIT_FOOT_CLASS;

  protected readonly name = signal('');
  protected readonly nameError = signal<string | null>(null);
  protected readonly nameBlocked = signal(false);
  protected readonly nameCheckPending = signal(false);
  private nameCheckRequest = 0;

  protected readonly services = signal<PrintServiceEntry[]>([]);
  /** Продукты выбранной типографии как они пришли: по ним помечаются свои строки в списке. */
  private readonly products = signal<PrintProductEntry[]>([]);
  protected readonly ownProductNames = computed(
    () =>
      new Set(
        this.products()
          .filter(p => p.ownerId)
          .map(p => p.productName),
      ),
  );

  private readonly ownProducts = signal<OwnPrintProduct[]>([]);
  protected readonly labOptions = computed(() => toLabOptions(this.services(), this.ownProducts(), this.services()));
  protected readonly servicesLoading = signal(true);
  protected readonly selectedService = signal<PrintServiceEntry | null>(null);
  protected readonly productError = signal(false);
  /** «Продолжить» без типографии: поле красное, пока фотограф её не выберет. */
  protected readonly serviceError = signal(false);
  protected readonly catalogFailed = computed(() => !this.servicesLoading() && !this.services().length && this.productError());

  /** Типография метки аккаунта; неактивной или пропавшей из каталога нет — тогда форма обычная. */
  protected readonly partnerLab = computed(() => {
    const key = this.identity.partnerKey();
    return key ? (findPartnerLab(this.services(), key) ?? null) : null;
  });

  /** Метка ещё неизвестна: заголовок и строка «Типография» ждут, чтобы обычный вид не мелькнул перед меченым. */
  protected readonly labPending = computed(
    () => (!this.identity.profile() && this.identity.loading()) || (!!this.identity.partnerKey() && this.servicesLoading()),
  );

  /** Заголовок хоста: имя типографии метки, пустой — пока метка неизвестна, `null` — свой заголовок хоста. */
  readonly labHeading = computed(() => this.partnerLab()?.name ?? (this.labPending() ? ' ' : null));

  // Профиль доехал после каталога: форма встаёт на типографию метки, стартовая и свои строки уходят.
  private readonly seatPartnerLabEffect = effect(() => {
    const lab = this.partnerLab();
    if (!lab) return;
    untracked(() => {
      if (this.selectedService()?.id === lab.id && !this.products().some(p => p.ownerId)) return;
      void this.applyService(lab, true);
    });
  });

  protected readonly cascade = new ProductCascade();
  /** Список «Продукт» открыт: строка технологии под полем скрыта под ним. */
  protected readonly productMenuOpen = signal(false);
  readonly resolvedProduct = computed(() => this.cascade.resolvedProduct());
  protected readonly labValue = computed(() => {
    const service = this.selectedService();
    return service
      ? labOptionValue(
          service.id,
          this.ownProducts().find(p => p.id === this.resolvedProduct()?.id),
        )
      : null;
  });

  /** Семейство формата и его название для «Выбора шаблона» — панель шага берёт их отсюда. */
  readonly projectFormat = computed(() => {
    const product = this.resolvedProduct();
    return product ? printProductFormat(product) : null;
  });

  readonly formatLabel = computed(() => this.resolvedProduct()?.formatName ?? '');

  private readonly selectedDesignId = signal<string | null>(null);
  readonly designId = this.selectedDesignId.asReadonly();
  private savedDesignId: string | null = null;

  /** Строку уже решили: гейтами ниже или самим фотографом — второй раз её никто не трогает. */
  private designDecided = false;
  /** Пак, о котором аналитика уже отчиталась в этой модалке; `undefined` — ещё ни о каком. */
  private announcedDesign: string | null | undefined;

  protected readonly spreads = signal(DEFAULT_SPREADS_TARGET);
  protected readonly spreadsLimitHit = signal(false);
  /** CRM request spreads, then the photographer's own count: a product switch re-clamps from it, not from the shown value. */
  private spreadsTarget: number | null = null;

  // Купленный пак живёт не среди своих шаблонов — строка и гейт формата ищут выбор в обоих списках.
  private readonly pickedDesign = computed(() => this.findDesign(this.designId()));

  /** Что строка обязана поднять сама: пак оставленного черновика, иначе запомненный в профиле. Оба
   *  идут одним путём — пока фотограф заводил шаблон в «Шаблонах», его выбор мог уйти на проверку,
   *  потерять право или разойтись с форматом, и черновику веры не больше, чем предпочтению. Экран со
   *  своим выбором (мастер шаблонов) решил всё сам — поднимать нечего. */
  private pickSavedDesignId(): string | null {
    const restore = this.restore();
    if (restore) return restore.designId;
    return this.initialDesignId() === undefined ? this.createProjectPreferences.readDesignId() : null;
  }

  // Запомненный шаблон встаёт в строку, как только его пак нашёлся в списках. Недоступного там нет —
  // и молчаливый «Без шаблона» остаётся как есть.
  private readonly restoreSavedDesignEffect = effect(() => {
    const saved = this.savedDesignId;
    if (!saved || this.designDecided) return;
    // Пока продукт не сложился, гейт формата сравнивать не с чем — эффект вернётся сюда, когда каскад встанет.
    const product = this.resolvedProduct();
    if (!product) return;
    const pack = this.findDesign(saved);
    if (!pack || !restorableDesign(pack)) return;
    this.designDecided = true;
    // Запомненный пак другого семейства формата молча уступает «Без шаблона»: в этой сессии его
    // никто не выбирал, тост был бы про чужое решение. Строку закрываем, чтобы смена формата её не воскресила.
    if (formatsConflict(designPackFormat(pack), packGateProductFormat(product))) return;
    if (isTrumeauKind(product.productKind)) {
      untracked(() => void this.restoreUnlessOtherKind(saved, pack, product));
      return;
    }

    untracked(() => this.selectedDesignId.set(saved));
  });

  /** Трюмо-лист судит пак видом, а не классом формата: альбомный пак молча не поднимается. */
  private async restoreUnlessOtherKind(
    saved: string,
    pack: DesignSummary | MyTemplate | LumirLibraryPack,
    product: PrintProductEntry,
  ): Promise<void> {
    const before = this.selectedDesignId();
    if (await this.packOfOtherKind(pack, product)) return;
    // Пока читался вид пака, фотограф мог выбрать сам или уйти с этого продукта.
    if (this.selectedDesignId() !== before || this.resolvedProduct()?.id !== product.id) return;
    this.selectedDesignId.set(saved);
  }

  /** Гейт вида трюмо-проекта; альбом и папка его не зовут, у них свой — класс формата. */
  private packOfOtherKind(pack: DesignSummary | MyTemplate | LumirLibraryPack, product: PrintProductEntry): Promise<boolean> {
    // Пак библиотеки несёт свой продукт так же, как свой пак: вид судится по продукту, а не по форме объекта.
    const packProductId = packPrintProductId(pack);
    return this.injector.get(PrintProductFormatService).trumeauPackKindConflict(product.productKind, packProductId);
  }

  /** Чипы формата не под выбранный шаблон: серые, но живые — клик объясняет, почему, и шаблон не трогает. */
  protected readonly formatHints = computed(() => {
    const hints = new Map<string, string>();
    const pack = this.pickedDesign();
    const packFormat = pack ? designPackFormat(pack) : null;
    if (!pack || !packFormat) return hints;
    const groups = this.cascade.formatGroups();
    // Размер пака — тот же, что в плите «Шаблон»; чипы сверяются по семейству формата, их размер может быть другим.
    const fit = groups.find(group => printProductFormat(group.defaultVariant) === packFormat) ?? this.cascade.selectedGroup();
    const packLabel =
      ('sizeLabel' in pack && pack.sizeLabel) ||
      resolvePackSizeLabelFromTemplates([{ width: pack.templateWidth, height: pack.templateHeight }]) ||
      (fit ? this.formatLabelOf(fit) : '');
    for (const group of groups) {
      if (!formatsConflict(packFormat, printProductFormat(group.defaultVariant))) continue;
      hints.set(group.key, `Шаблон сделан под ${packLabel}\nЧтобы выбрать ${this.formatLabelOf(group)}, поменяйте шаблон`);
    }

    return hints;
  });

  /** Чип формата, под которым сейчас стоит подсказка. */
  protected readonly formatHintAt = signal<string | null>(null);
  private formatHintTimer: ReturnType<typeof setTimeout> | undefined;

  readonly designName = computed(() => this.pickedDesign()?.name ?? 'Свободный дизайн');
  readonly designCover = computed(() => this.pickedDesign()?.coverUrl || FREE_DESIGN_COVER);
  readonly packCover = computed(() => this.pickedDesign()?.coverUrl || null);

  protected readonly kindLabelOf = printProductKindLabel;

  protected readonly kindLabel = computed(() => {
    const kind = this.cascade.kindValue();
    return kind ? printProductKindLabel(kind) : 'Выберите вид изделия';
  });

  protected readonly nameLabel = computed(() => this.cascade.nameValue() ?? 'Выберите продукт');

  /** Типография есть, продукта нет: ни отправить, ни выбрать шаблон — сетке нечем отсеять паки по формату. */
  readonly templateBlocked = computed(() => !!this.selectedService() && !this.resolvedProduct());
  // Без типографии кнопка живая — клик подсвечивает поле; с типографией без продукта отправлять нечего.
  protected readonly submitBlocked = computed(() => this.nameCheckPending() || this.nameBlocked() || this.templateBlocked());
  /** Лаба с несколькими видами изделий: пока вид не выбран, «Продолжить» объясняет, чего ждёт. */
  protected readonly submitHint = computed(() =>
    this.cascade.kindVisible() && !this.cascade.kindValue() ? 'Выберите вид изделия' : '',
  );

  protected readonly spreadsMin = computed(() => this.resolvedProduct()?.minSpreads ?? 1);
  protected readonly spreadsMax = computed(() => this.resolvedProduct()?.maxSpreads ?? 30);
  protected readonly spreadStep = computed(() => this.resolvedProduct()?.spreadStep ?? 1);
  /** Изделие на один разворот (папка): считать нечего, строки количества нет. */
  protected readonly singleSpread = computed(() => this.spreadsMin() === 1 && this.spreadsMax() === 1);

  protected readonly pagesHint = computed(() => {
    const pages = this.spreads() * 2;
    return `${pages} ${pluralRu(pages, ['страница', 'страницы', 'страниц'])}`;
  });

  // On variant change the count re-clamps into the new format's bounds (develop parity).
  private readonly clampSpreadsEffect = effect(() => {
    const product = this.resolvedProduct();
    if (!product) return;
    untracked(() => {
      const current = this.spreads();
      const target = this.spreadsTarget ?? current;
      const clamped = clampSpreads(target, product);
      this.spreadsLimitHit.set(clamped !== current && clamped !== target);
      this.spreads.set(clamped);
    });
  });

  /** Формат, на котором каскад уже стоял; `undefined` — он не вставал ещё ни разу. */
  private settledFormat: BookFormat | null | undefined;
  /** Трюмо-лист отдаёт формату null — без вида смена «альбом без размеров → трюмо» не видна. */
  private settledTrumeau = false;

  // Смена семейства формата любым уровнем каскада — от типографии до варианта — уносит шаблон другого размера.
  private readonly resetDesignOnFormatChangeEffect = effect(() => {
    const product = this.resolvedProduct();
    if (!product) return;
    // Класс формата трюмо-листа пак не судит (null): смена трюмо-продукта шаблон не уносит.
    const format = packGateProductFormat(product);
    const trumeau = isTrumeauKind(product.productKind);
    const settled = this.settledFormat;
    const settledTrumeau = this.settledTrumeau;
    this.settledFormat = format;
    this.settledTrumeau = trumeau;
    // Первый посчитанный формат — это сид, а не смена: иначе восстановленный шаблон сбрасывался бы на открытии.
    // Вид сид всё же судит на трюмо-продукте: форму могли открыть с альбомным паком.
    if (settled === undefined) {
      if (trumeau) untracked(() => this.dropDesignOfOtherFormat(format));
      return;
    }

    if (settled === format && settledTrumeau === trumeau) return;
    untracked(() => this.dropDesignOfOtherFormat(format));
  });

  /** Возврат уже открыл «Выбор шаблона» — второй раз модалка сама не открывается. */
  private pickerReopened = false;

  // Фотограф ушёл в «Шаблоны» из «Выбора шаблона» — возврат отдаёт ему ту же сцену. Ждём продукт:
  // без него у модалки выбора нет ни семейства формата, ни его названия для вкладки.
  private readonly reopenPickerEffect = effect(() => {
    if (!this.restore()?.pickerOpen || this.pickerReopened) return;
    if (!this.resolvedProduct()) return;
    this.pickerReopened = true;
    untracked(() => this.openTemplatePicker());
  });

  // Входы доезжают до формы только после конструктора — стартовое состояние поднимается здесь.
  ngOnInit(): void {
    const restore = this.restore();
    this.name.set(restore?.name ?? '');
    // Request spreads are the target before any lab: stashes, lab picks and the clamp all start from them.
    if (restore?.fromRequest && restore.productId === null && restore.spreads !== null) {
      this.spreads.set(restore.spreads);
      this.spreadsTarget = restore.spreads;
    }

    this.selectedDesignId.set(this.initialDesignId() ?? null);
    this.savedDesignId = this.pickSavedDesignId();
    this.wizardAnalytics.createProjectClicked();
    // Модалка, открытая любым другим путём, начинает с чистого листа: оставленный черновик до неё не доживает.
    if (!this.restore()) this.draftStash.clear();
    // Свои паки строка «Шаблон» читает из того же списка, что и раздел «Шаблоны», — сюда приходят и мимо него.
    void this.designs.loadDesigns().catch(() => undefined);
    const snapshot = this.defaultPrintProduct.peekCatalog();
    if (snapshot?.services.length) {
      this.services.set(snapshot.services);
      const initialService = this.pickInitialService(snapshot.services);
      this.servicesLoading.set(false);
      this.selectedService.set(initialService);
      void firstValueFrom(this.printCatalog.listOwnProducts())
        .then(own => this.ownProducts.set(own))
        .catch(() => undefined);
      if (initialService && initialService.id === snapshot.preferredService?.id) {
        const products = this.offered(snapshot.products);
        this.products.set(products);
        this.cascade.setProducts(products);
        this.productError.set(products.length === 0);
        if (products.length) this.seedInitialSelection(products, initialService.id);
      } else if (initialService) {
        void this.applyService(initialService, true);
      }
    } else {
      void this.loadServices();
    }
  }

  private async loadServices(): Promise<void> {
    this.servicesLoading.set(true);
    try {
      // Одно чтение своих продуктов на список типографий и продукты стартовой: иначе own/products уходит дважды.
      const ownProducts = this.printCatalog.listOwnProducts().pipe(shareReplay(1));
      const [services, own] = await Promise.all([
        firstValueFrom(this.printCatalog.getPrintServices(ownProducts)),
        firstValueFrom(ownProducts).catch(() => [] as OwnPrintProduct[]),
      ]);
      this.services.set(services);
      this.ownProducts.set(own);
      this.productError.set(services.length === 0);
      const initialService = this.pickInitialService(services);
      if (initialService) await this.applyService(initialService, true, ownProducts);
    } catch {
      this.services.set([]);
      this.productError.set(true);
    } finally {
      this.servicesLoading.set(false);
    }
  }

  protected retryLoadIfNeeded(): void {
    if (!this.servicesLoading() && !this.services().length) void this.loadServices();
  }

  private async applyService(
    service: PrintServiceEntry,
    initial = false,
    ownProducts?: Observable<OwnPrintProduct[]>,
  ): Promise<void> {
    this.selectedService.set(service);
    this.serviceError.set(false);
    this.products.set([]);
    this.cascade.setProducts([]);
    this.productError.set(false);
    try {
      const read = ownProducts
        ? this.printCatalog.getPrintProducts(service.id, ownProducts)
        : this.printCatalog.getPrintProducts(service.id);
      const products = this.offered(await firstValueFrom(read));
      if (this.selectedService()?.id !== service.id) return;
      this.products.set(products);
      this.cascade.setProducts(products);
      this.productError.set(products.length === 0);
      if (products.length) {
        if (initial) this.seedInitialSelection(products, service.id);
        else this.seedDefaultSelection(products);
      }
    } catch {
      if (this.selectedService()?.id !== service.id) return;
      this.productError.set(true);
    }
  }

  private seedDefaultSelection(products: PrintProductEntry[]): void {
    // Типография с несколькими видами изделий: вид выбирает фотограф, продукт без него не предвыбирается.
    if (this.cascade.kindVisible()) return;
    this.cascade.seedFromProduct(pickPreferredProduct(products, this.packFormat()));
    const resolved = this.cascade.resolvedProduct();
    if (!resolved) return;
    this.spreads.set(resolved.defaultSpreads ?? DEFAULT_SPREADS_TARGET);
    this.spreadsLimitHit.set(false);
  }

  private seedInitialSelection(products: PrintProductEntry[], serviceId: string): void {
    // Оставленный черновик старше запомненного выбора: фотограф ушёл в «Шаблоны» ровно с этим продуктом,
    // и каскад раскрывается обратно в ту же позицию — вид, формат, вариант, основа и толщина.
    const restore = this.restore();
    const restored = restore ? (products.find(product => product.id === restore.productId) ?? null) : null;
    const preference = this.savedPreference;
    const preferred =
      preference?.serviceId === serviceId ? (products.find(product => product.id === preference.productId) ?? null) : null;
    // A remembered product of another format family must not preselect under a picked pack.
    const packFormat = this.packFormat();
    const savedProduct = preferred && packFormat && printProductFormat(preferred) !== packFormat ? null : preferred;
    this.cascade.seedFromProduct(restored ?? savedProduct ?? pickPreferredProduct(products, packFormat));
    const resolved = this.cascade.resolvedProduct();
    if (!resolved) return;
    // A CRM request's spreads (or the hand edit over them) already sit in spreadsTarget since ngOnInit.
    const keptSpreads = restored ? (restore?.spreads ?? null) : this.spreadsTarget;
    let initialSpreads = DEFAULT_SPREADS_TARGET;
    if (keptSpreads !== null) initialSpreads = keptSpreads;
    else if (savedProduct && preference) initialSpreads = preference.spreads;
    this.spreads.set(clampSpreads(initialSpreads, resolved));
    this.spreadsLimitHit.set(false);
  }

  /** Метка сужает каталог до продуктов её типографии: своих строк под ней нет. */
  private offered(products: PrintProductEntry[]): PrintProductEntry[] {
    return this.partnerLab() ? products.filter(p => !p.ownerId) : products;
  }

  private pickInitialService(services: readonly PrintServiceEntry[]): PrintServiceEntry | null {
    const lab = this.partnerLab();
    if (lab) return lab;
    const savedServiceId = this.restore()?.serviceId ?? this.savedPreference?.serviceId;
    return services.find(service => service.id === savedServiceId) ?? null;
  }

  /** Строка поля: каталожная типография или свой продукт — свой раскрывает каскад сразу на себе. */
  protected pickLab(id: string | null): void {
    const pick = resolveLabPick(id, this.labOptions());
    const service = pick && this.services().find(s => s.id === pick.serviceId);
    if (!pick || !service) return;
    this.serviceError.set(false);
    if (!pick.productId && service.id === this.selectedService()?.id) return;
    void this.applyService(service).then(() => {
      const product = pick.productId && this.products().find(p => p.id === pick.productId);
      if (!product || this.selectedService()?.id !== service.id) return;
      this.cascade.seedFromProduct(product);
      this.spreads.set(clampSpreads(product.defaultSpreads ?? DEFAULT_SPREADS_TARGET, product));
    });
  }

  protected editOwnLab(productId: string): void {
    this.stashDraft(false);
    this.closeSheet();
    void this.router.navigate(['/typographies/products', productId], { queryParams: { from: 'dialog' } });
  }

  /** Корзина у своей строки: удаляются её параметры, не вся типография. */
  protected removeOwnLab(productId: string): void {
    const option = this.labOptions().find(o => o.id === productId);
    if (this.askingDelete() || !option?.serviceId) return;
    void this.confirmDeleteOwnProduct(productId, option.serviceId, option.label ?? option.name);
  }

  /** Строка «Шаблон» ведёт в «Выбор шаблона»: та отдаёт выбор на «Выбрать» и молчит на «Отменить». */
  protected openTemplatePicker(): void {
    if (!this.requireLab()) return;
    const product = this.resolvedProduct();
    const ref = this.dialogs.open<TemplatePickerDialogContext, TemplatePickerResult>(TemplatePickerDialogComponent, this.vcr, {
      projectFormat: product ? printProductFormat(product) : null,
      formatLabel: product?.formatName ?? '',
      ...(product && { projectKind: product.productKind ?? null }),
      designId: this.designId(),
      // Проекта ещё нет — потраченный кредит аренды до него не доедет ни при каком раскладе.
      projectId: null,
    });
    ref.closed$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (!result) return;
      // Фотограф ушёл заводить шаблон — обе модалки уходят со сцены, но заполненное ждёт его в разделе
      // «Шаблоны» под кнопкой «Вернуться к созданию проекта».
      if (result === 'designs') {
        this.stashDraft(true);
        this.closeSheet();
      } else {
        this.applyPick(result);
      }
    });
  }

  /** Выбор из «Выбора шаблона» — модалки строки или панели шага. Защёлка не даёт запомненному паку его перебить. */
  applyPick(result: Exclude<TemplatePickerResult, 'designs'>): void {
    this.designDecided = true;
    this.selectedDesignId.set(result.designId);
    this.announceDesign(result.designId, result.designId ? 'catalog_picked' : 'default_kept');
  }

  /** Уход с формы в другой раздел: модалка закрывается, шаг мастера просто уступает место маршруту. */
  private closeSheet(): void {
    if (this.chrome() === 'sheet') this.dialogRef?.close(undefined);
  }

  /**
   * Выбор шаблона проекта живёт здесь: у мастера шага «Выбор шаблона» больше нет. Событие одно на пак:
   * «Выбрать» отчитывается о самом выборе, `submit()` — о том, что реально уехало в проект, и без него
   * дефолтный путь (пикер не открывали ни разу) не порождал бы ни одного события — а это масса созданий.
   * Переоткрыть модалку и подтвердить тот же пак — не второй выбор, и воронку он бы задвоил.
   */
  private announceDesign(designId: string | null, trigger: TemplateSelectTrigger): void {
    if (this.announcedDesign === designId) return;
    this.announcedDesign = designId;
    this.wizardAnalytics.templateSelected({
      mode: 'select',
      packId: designId ?? FREE_PACK_ID,
      isDefaultPack: !designId,
      surface: 'project_wizard',
      trigger,
    });
  }

  /** Выбор ищется в обоих списках: свои паки и купленные лежат врозь; не нашёлся — это пак из предложения
   *  библиотеки Люмир, права на него выдаются при создании проекта. */
  private findDesign(id: string | null): DesignSummary | MyTemplate | LumirLibraryPack | null {
    if (!id) return null;
    return (
      this.designs.designs().find(design => design.id === id) ??
      this.shop.myTemplates().find(pack => pack.id === id) ??
      libraryOffer(this.library.packs(), this.shop.myTemplates()).find(pack => pack.id === id) ??
      null
    );
  }

  /** Формат сменился, пока списки паков ещё грузились: судить было не по чему. `undefined` — долга нет. */
  private unjudgedFormat: BookFormat | null | undefined;

  private dropDesignOfOtherFormat(productFormat: BookFormat | null): void {
    const design = this.pickedDesign();
    if (!design) {
      // Пак ещё не доехал — вердикт откладывается, иначе шаблон чужого размера уезжает в проект молча.
      this.unjudgedFormat = this.designId() ? productFormat : undefined;
      return;
    }

    this.unjudgedFormat = undefined;
    if (formatsConflict(designPackFormat(design), productFormat)) {
      this.dropDesign(design, 'другого размера');
      return;
    }

    const product = this.resolvedProduct();
    if (product && isTrumeauKind(product.productKind)) void this.dropDesignOfOtherKind(design, product);
  }

  private async dropDesignOfOtherKind(
    design: DesignSummary | MyTemplate | LumirLibraryPack,
    product: PrintProductEntry,
  ): Promise<void> {
    if (!(await this.packOfOtherKind(design, product))) return;
    // Вердикт опоздал: выбор или продукт уже сменились — судить их будет следующий проход.
    if (this.designId() !== design.id || this.resolvedProduct()?.id !== product.id) return;
    this.dropDesign(design, 'сделан не для трюмо');
  }

  /** `reason` — причина отказа в тосте: размер и вид изделия сбрасывают пак одинаково, но называются по-разному. */
  private dropDesign(
    design: DesignSummary | MyTemplate | LumirLibraryPack,
    reason: 'другого размера' | 'сделан не для трюмо',
  ): void {
    this.selectedDesignId.set(null);
    this.toast.warn(`«${design.name}» ${reason} — шаблон сброшен на «Свободный дизайн»`);
  }

  /** Отложенный вердикт выносится, как только пак нашёлся в загруженных списках. */
  private readonly lateFormatVerdictEffect = effect(() => {
    const design = this.pickedDesign();
    if (!design || this.unjudgedFormat === undefined) return;
    const format = this.unjudgedFormat;
    untracked(() => this.dropDesignOfOtherFormat(format));
  });

  protected requestOtherService(): void {
    this.telegramContact.open('otherService');
  }

  /** Снимок заполненного: с ним модалка откроется заново на том же месте, куда бы фотограф ни ушёл. */
  private stashDraft(pickerOpen: boolean): void {
    this.draftStash.stash({
      name: this.name(),
      serviceId: this.selectedService()?.id ?? null,
      productId: this.resolvedProduct()?.id ?? null,
      spreads: this.spreads(),
      designId: this.designId(),
      pickerOpen,
      ...requestOf(this.restore()),
    });
  }

  /** Проекты на продукте считаются ДО окна: дописать предупреждение в уже открытый диалог нельзя. */
  private async confirmDeleteOwnProduct(productId: string, serviceId: string, label: string): Promise<void> {
    this.askingDelete.set(productId);
    const used = await this.usage.countProjectsOn([productId]);
    this.askingDelete.set(null);

    const ref = this.dialogs.open<ConfirmDialogContext, boolean>(
      ConfirmDialogComponent,
      this.vcr,
      {
        title: `Удалить «${label}»?`,
        message: ownCatalogDeleteWarning(used),
        confirmLabel: 'Удалить параметры',
        danger: true,
      },
      { hasBackdrop: true },
    );
    ref.closed$.pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(confirmed => {
      if (confirmed) void this.removeOwnProduct(productId, serviceId);
    });
  }

  /** Последний продукт своей типографии уносит и её: пустая типография в поле не видна и не нужна. */
  private async removeOwnProduct(productId: string, serviceId: string): Promise<void> {
    const siblings = this.ownProducts().filter(p => p.printServiceId === serviceId);
    const last = siblings.every(p => p.id === productId);
    try {
      await firstValueFrom(
        last ? this.printCatalog.deleteServiceWithProducts(serviceId, siblings) : this.printCatalog.deleteProduct(productId),
      );
    } catch {
      this.toast.error('Не удалось удалить параметры — попробуйте ещё раз');
      return;
    }

    // The cached snapshot still lists the deleted lab; the next open would preselect it from there.
    this.defaultPrintProduct.invalidateCatalog();
    if (this.labValue() === productId) {
      this.selectedService.set(null);
      this.products.set([]);
      this.cascade.setProducts([]);
    }

    this.ownProducts.set(this.ownProducts().filter(p => p.id !== productId));
    if (last) this.services.set(this.services().filter(s => s.id !== serviceId));
  }

  /**
   * Фотограф уходит заводить свою типографию или свой продукт под выбранной: заполненное ждёт его
   * в черновике, и после сохранения модалка откроется заново с уже выбранным продуктом.
   */
  protected goToOwnCatalog(query = ''): void {
    this.stashDraft(false);
    this.closeSheet();
    void this.router.navigate(['/typographies/new'], {
      queryParams: { from: 'dialog', ...(query ? { name: query } : {}) },
    });
  }

  protected requestOtherFormat(): void {
    this.telegramContact.open('otherFormat');
  }

  protected technologyLabelOf(technology: string): string {
    return technologyLabel(technology);
  }

  protected formatLabelOf(group: ProductVariantGroup): string {
    return formatLabel(group, this.cascade.formatGroups());
  }

  /** Клик по серому чипу — подсказка; повторный клик, уход мыши или пауза её убирают. */
  protected toggleFormatHint(key: string): void {
    clearTimeout(this.formatHintTimer);
    const open = this.formatHintAt() !== key;
    this.formatHintAt.set(open ? key : null);
    if (open) this.formatHintTimer = setTimeout(() => this.formatHintAt.set(null), FORMAT_HINT_MS);
  }

  protected onFormatChipLeave(event: PointerEvent): void {
    if (event.pointerType === 'touch') return;
    clearTimeout(this.formatHintTimer);
    this.formatHintAt.set(null);
  }

  protected onNameInput(value: string): void {
    this.name.set(value);
    this.nameCheckRequest += 1;
    this.nameCheckPending.set(false);
    this.nameBlocked.set(false);
    this.nameError.set(null);
  }

  // Счётчик отдаёт только значения внутри границ — своя проверка не нужна, снимаем лишь подсказку о пределе.
  protected setSpreads(next: number): void {
    this.spreads.set(next);
    if (this.spreadsTarget !== null) this.spreadsTarget = next;
    this.spreadsLimitHit.set(false);
  }

  /** Типография обязательна: без printProductId сборка альбома уйдёт в формат по умолчанию. Пустое поле краснеет. */
  requireLab(): boolean {
    if (this.selectedService()) return true;
    this.serviceError.set(true);
    return false;
  }

  protected async submit(): Promise<void> {
    const name = this.name().trim();
    if (!name) {
      this.nameError.set('Введите название проекта');
      return;
    }

    if (!this.requireLab()) return;

    const product = this.resolvedProduct();
    if (!product) return;

    if (this.nameCheckPending()) return;

    const requestId = ++this.nameCheckRequest;
    this.nameCheckPending.set(true);
    this.nameBlocked.set(false);
    this.nameError.set(null);

    const verdict = await this.projects.checkProjectName(name, this.ownName());
    if (requestId !== this.nameCheckRequest) return;
    this.nameCheckPending.set(false);
    if (verdict !== 'free') {
      this.nameBlocked.set(verdict === 'taken');
      this.nameError.set(PROJECT_NAME_ERROR[verdict]);
      return;
    }

    // Пак уезжает в проект — воронка обязана увидеть его и на дефолтном пути, где пикер не открывали.
    this.announceDesign(this.designId(), 'default_kept');

    const draft: CreateProjectDraft = {
      name,
      service: this.selectedService()?.name ?? '',
      product: productTitle(product),
      spreads: this.spreads(),
      productEntry: product,
      flowMode: 'studentAssembles',
      designId: this.designId(),
      ...requestOf(this.restore()),
    };
    this.submitted.emit(draft);
  }

  protected cancel(): void {
    this.nameCheckRequest += 1;
    this.cancelled.emit();
  }
}
